import { useState } from 'react'

import { Link04Icon, Upload04Icon } from '@hugeicons/react'
import { useController, useFormContext } from 'react-hook-form'

import { AppIcon } from '@/shared/ui/icons'
import { Button } from '@/shared/ui/Button'
import { controlVariants } from '@/shared/ui/Form/control-styles'
import { cn } from '@/lib/utils'

type MissionLinkEvidenceControlsProps = {
  isSubmitting: boolean
  isReadonly: boolean
  isCapacityFull: boolean
  submitLabel: string
}

const isValidUrl = (value: string) => {
  const trimmed = value.trim()
  if (!trimmed) return false

  try {
    const parsed = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`)
    return Boolean(parsed.hostname)
  } catch {
    return false
  }
}

export const MissionLinkEvidenceControls = ({
  isSubmitting,
  isReadonly,
  isCapacityFull,
  submitLabel
}: MissionLinkEvidenceControlsProps) => {
  const [isConfirmed, setIsConfirmed] = useState(false)
  const { control } = useFormContext()
  const {
    field: { value, onChange, onBlur, ref }
  } = useController({
    name: 'content',
    control,
    rules: {
      required: true,
      validate: fieldValue => isValidUrl(String(fieldValue ?? '')) || 'Informe um link válido'
    }
  })

  const linkValue = String(value ?? '').trim()
  const linkIsValid = isValidUrl(linkValue)
  const isBusy = isSubmitting
  const isDisabled = isBusy || isCapacityFull || isReadonly

  const handleConfirm = () => {
    if (!linkIsValid) return
    setIsConfirmed(true)
  }

  const handleEdit = () => {
    setIsConfirmed(false)
  }

  const handlePaste = async () => {
    if (typeof navigator === 'undefined' || !navigator.clipboard?.readText) return

    try {
      const text = (await navigator.clipboard.readText()).trim()
      if (text) onChange(text)
    } catch {
      // iOS só libera leitura da área de transferência em gesto explícito;
      // falha silenciosa se o usuário negar ou o navegador bloquear.
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {isConfirmed ? (
        <div className="flex flex-col gap-2">
          <span className="text-caps font-bold uppercase tracking-wide text-gray-700">
            Link confirmado
          </span>
          <a
            href={linkValue.startsWith('http') ? linkValue : `https://${linkValue}`}
            target="_blank"
            rel="noopener noreferrer"
            className="break-all text-sm font-semibold text-primary"
          >
            {linkValue}
          </a>
          {!isDisabled && (
            <button
              type="button"
              onClick={handleEdit}
              className="self-start border-0 bg-transparent p-0 text-xs font-semibold text-gray-700 underline"
            >
              Alterar link
            </button>
          )}
        </div>
      ) : (
        <>
          <label
            htmlFor="mission-evidence-link"
            className="flex items-center gap-2 text-sm font-semibold text-gray-900"
          >
            <Link04Icon size={18} variant="duotone" className="text-primary" aria-hidden />
            Cole o link da sua evidência
          </label>

          <div className="relative">
            <input
              ref={ref}
              id="mission-evidence-link"
              type="url"
              inputMode="url"
              autoComplete="url"
              value={value ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              disabled={isDisabled}
              placeholder="https://exemplo.com/sua-evidencia"
              aria-label="Link da evidência"
              className={cn(
                controlVariants({ size: 'lg' }),
                'min-h-12 w-full pr-[5.5rem] text-base'
              )}
            />

            {!isDisabled && (
              <button
                type="button"
                onClick={() => {
                  void handlePaste()
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-[0.625rem] border-0 bg-[#f0f0f4] px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm transition-colors hover:bg-[#e8e8ee]"
              >
                Colar
              </button>
            )}
          </div>

          <p className="m-0 text-xs text-gray-600">
            Use o endereço completo da página ou arquivo que comprova a missão.
          </p>
        </>
      )}

      <Button
        type={isConfirmed ? 'submit' : 'button'}
        variant="primary"
        size="md"
        showTrailingIcon={false}
        className="w-full justify-center"
        disabled={isDisabled || (!isConfirmed && !linkIsValid)}
        onClick={isConfirmed ? undefined : handleConfirm}
        leadingIcon={
          isBusy ? (
            <AppIcon name="spinner-third" />
          ) : isConfirmed ? (
            <Upload04Icon size={16} variant="stroke" />
          ) : (
            <Link04Icon size={16} variant="stroke" />
          )
        }
      >
        {isSubmitting
          ? 'Enviando...'
          : isConfirmed
            ? submitLabel
            : 'Confirmar link'}
      </Button>
    </div>
  )
}
